import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMuiController } from "@sinch/components";
import { ChipCheckboxInput } from "@sinch/components/form/ChipCheckboxInput/ChipCheckboxInput";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MessageFormFields } from "./formSchema";
import {
  applyAudience,
  AudienceAttendance,
  AudiencePositions,
  AudienceSelection,
  AudienceStatus,
  selectAudienceUserIds,
} from "./jobAudience";

interface JobAudienceFilterProps {
  attendances: AudienceAttendance[];
  initialSelection: AudienceSelection;
}

interface AudienceOption<T> {
  value: T;
  label: string;
  count: number;
}

/**
 * One row of checkbox chips between the recipients label and input, two groups (attendance
 * status, position cancelled or not) split by a divider. Toggling a chip
 * replaces the job's people in the recipients field with the chosen audience.
 * A group is only shown when it actually splits the job's people, so a job with
 * no invited staff and no cancelled positions renders nothing.
 */
export const JobAudienceFilter: FC<JobAudienceFilterProps> = ({ attendances, initialSelection }) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<AudienceSelection>(initialSelection);
  const {
    field: { value: recipients, onChange: setRecipients },
  } = useMuiController<MessageFormFields, "recipients">({ name: "recipients" });

  const countUsers = useCallback(
    (statuses: AudienceStatus[], positions: AudiencePositions[]) =>
      selectAudienceUserIds(attendances, { statuses, positions }).length,
    [attendances]
  );

  // Each count respects the selection of the other group, so it says how many
  // people the option contributes to the current audience.
  const statusOptions = useMemo<AudienceOption<AudienceStatus>[]>(
    () => [
      {
        value: AudienceStatus.Confirmed,
        label: t("MessageDialog.audienceConfirmed"),
        count: countUsers([AudienceStatus.Confirmed], selection.positions),
      },
      {
        value: AudienceStatus.Invited,
        label: t("MessageDialog.audienceInvited"),
        count: countUsers([AudienceStatus.Invited], selection.positions),
      },
    ],
    [countUsers, selection.positions, t]
  );

  const positionOptions = useMemo<AudienceOption<AudiencePositions>[]>(
    () => [
      {
        value: AudiencePositions.Active,
        label: t("MessageDialog.audienceActive"),
        count: countUsers(selection.statuses, [AudiencePositions.Active]),
      },
      {
        value: AudiencePositions.Cancelled,
        label: t("MessageDialog.audienceCancelled"),
        count: countUsers(selection.statuses, [AudiencePositions.Cancelled]),
      },
    ],
    [countUsers, selection.statuses, t]
  );

  const hasInvited = attendances.some(({ status }) => status === AudienceStatus.Invited);
  const hasConfirmed = attendances.some(({ status }) => status === AudienceStatus.Confirmed);
  const hasCancelled = attendances.some(({ positions }) => positions === AudiencePositions.Cancelled);
  const hasActive = attendances.some(({ positions }) => positions === AudiencePositions.Active);
  const showStatuses = hasInvited && hasConfirmed;
  const showPositions = hasCancelled && hasActive;
  const isEmptyAudience = countUsers(selection.statuses, selection.positions) === 0;

  const handleChange = useCallback(
    (next: AudienceSelection) => {
      setSelection(next);
      setRecipients(applyAudience(recipients, attendances, next));
    },
    [attendances, recipients, setRecipients]
  );

  if (!showStatuses && !showPositions) {
    return null;
  }

  return (
    <Stack spacing={0.5} sx={{ pb: 1 }}>
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5}>
        <span>{t("MessageDialog.audienceTitle")}</span>
        {showStatuses && (
          <AudienceToggleGroup
            onChange={(statuses) => handleChange({ ...selection, statuses })}
            options={statusOptions}
            selected={selection.statuses}
          />
        )}
        {showStatuses && showPositions && <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />}
        {showPositions && (
          <AudienceToggleGroup
            onChange={(positions) => handleChange({ ...selection, positions })}
            options={positionOptions}
            selected={selection.positions}
          />
        )}
      </Stack>
      {isEmptyAudience && (
        <Typography color="info.main" variant="inherit">
          {t("MessageDialog.audienceEmpty")}
        </Typography>
      )}
    </Stack>
  );
};

interface AudienceToggleGroupProps<T> {
  options: AudienceOption<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
}

/**
 * Checkbox chips of one group. Every chip can be turned off; an empty group
 * matches nobody, which removes all of the job's people from the recipients.
 */
const AudienceToggleGroup = <T extends string>({ options, selected, onChange }: AudienceToggleGroupProps<T>) => {
  const toggle = (value: T) =>
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);

  return (
    <>
      {options.map(({ value, label, count }) => {
        const isSelected = selected.includes(value);

        return (
          // ChipCheckboxInput renders a full-width FormControl, the Box keeps it chip-sized
          <Box key={value}>
            <ChipCheckboxInput
              label={`${label} (${count})`}
              onChange={() => toggle(value)}
              onDelete={isSelected ? () => toggle(value) : undefined}
              size="small"
              value={isSelected}
            />
          </Box>
        );
      })}
    </>
  );
};
